<script setup lang="ts">
    import { ref, computed, watch } from 'vue';
    import { useDebounceFn } from '@vueuse/core';

    const props = defineProps<{
        state: 'idle' | 'saving' | 'error';
    }>();

    const showSaved = ref(false);
    const SAVED_FADE_OUT_MILLISECONDS = 5000;

    const hideSavedAfterTimeout = useDebounceFn(
        () => (showSaved.value = false),
        SAVED_FADE_OUT_MILLISECONDS,
    );

    watch(
        () => props.state,
        (newValue, oldValue) => {
            if (oldValue === 'saving' && newValue === 'idle') {
                showSaved.value = true;
                hideSavedAfterTimeout();
            }
            if (newValue === 'saving') {
                showSaved.value = false;
            }
        },
    );

    const srStatus = computed<'saving' | 'saved' | 'error' | 'idle'>(() => {
        if (props.state === 'saving') return 'saving';
        if (props.state === 'error') return 'error';
        if (showSaved.value) return 'saved';
        return 'idle';
    });
</script>

<template>
    <div
        class="save-status-indicator text-secondary-text"
        data-testid="save-status"
        :data-sr-status="srStatus"
    >
        <div v-if="state === 'saving'">
            <v-icon size="20" icon="mdi-sync" />
            <span>Saving …</span>
            <app-tooltip :activator="'parent'">
                Changes are being saved. This can take a few seconds.
            </app-tooltip>
        </div>
        <div v-else-if="state === 'error'">
            <v-icon size="20" icon="mdi-alert-circle-outline" color="error" />
            <span>Save failed</span>
            <app-tooltip :activator="'parent'">
                We couldn't save your changes. Retrying…
            </app-tooltip>
        </div>
        <div v-else-if="showSaved">
            <v-icon size="20" icon="mdi-cloud-check" color="success" />
            <span>Saved</span>
            <app-tooltip :activator="'parent'">All changes are saved.</app-tooltip>
        </div>
    </div>
</template>

<style scoped>
    .save-status-indicator {
        font-size: 14px;
    }

    .save-status-indicator > div {
        display: flex;
        align-items: center;
        gap: 8px;
    }
</style>
